import { ColDef, ColGroupDef, GridOptions } from 'ag-grid-community'
import { TradingEntityClearingDetail } from 'api'
import { Translation, Locale } from 'locale'
import {
  gridOptionsBase,
  TypedValueGetterParams,
  withAllContext,
  createActionsRenderer,
  StatusCellRenderer
} from 'view/components/datagrid'
import { TradingEntityClearingPanelActionsButton } from './TradingEntityClearingPanelActionsButton'

import { getEntityStatusColor, getEntityStatusActiveSuspendedText } from 'view/shared/datagrid'
import { ViewTradingEntityClearingPanelActionsButton } from './ViewTradingEntityClearingPanelActionsButton'

export enum TradingEntityClearingPanelGridColumnId {
  STATUS = 'STATUS',
  CLEARING_RELATIONSHIP_ACTIONS = 'CLEARING_RELATIONSHIP_ACTIONS',
  CLEARING_HOUSE_NAME = 'CLEARING_HOUSE_NAME',
  SWIFT_ID = 'SWIFT_ID',
  PRODUCT_SECTOR_COUNT = 'PRODUCT_SECTOR_COUNT',
  SPONSOR_NAME = 'SPONSOR_NAME'
}

export const DIRECT_PREFIX = 'DIRECT_'
export const SPONSORED_PREFIX = 'SPONSORED_'

const commonTradingEntityClearingPanelColumnDefs = (prefix: string): (ColGroupDef | ColDef)[] => [
  {
    colId: `${prefix}${TradingEntityClearingPanelGridColumnId.CLEARING_RELATIONSHIP_ACTIONS}`,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    equals: () => false,
    width: 150,
    minWidth: 130,
    cellClass: 'action-cell',
    cellRenderer: withAllContext(createActionsRenderer(TradingEntityClearingPanelActionsButton)),
    pinned: 'left',
    lockPinned: true,
    enableRowGroup: false,
    lockVisible: true,
    filter: false,
    sortable: false
  },
  {
    colId: TradingEntityClearingPanelGridColumnId.CLEARING_RELATIONSHIP_ACTIONS,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    equals: () => false,
    width: 150,
    minWidth: 130,
    cellClass: 'action-cell',
    cellRenderer: withAllContext(createActionsRenderer(ViewTradingEntityClearingPanelActionsButton)),
    pinned: 'left',
    lockPinned: true,
    enableRowGroup: false,
    lockVisible: true,
    filter: false,
    sortable: false
  },
  {
    colId: `${prefix}${TradingEntityClearingPanelGridColumnId.STATUS}`,
    valueGetter: (params: TypedValueGetterParams<TradingEntityClearingDetail>) =>
      params.data && getEntityStatusActiveSuspendedText(params.data.tradingEntityClearing.status),
    headerName: Locale.translate(Translation.TC_STATUS),
    width: 100,
    filter: 'agSetColumnFilter',
    cellRenderer: StatusCellRenderer,
    cellRendererParams: {
      getStatusColor: (data: TradingEntityClearingDetail) => getEntityStatusColor(data.tradingEntityClearing.status)
    },
    equals: () => false,
    lockVisible: true
  },
  {
    colId: `${prefix}${TradingEntityClearingPanelGridColumnId.CLEARING_HOUSE_NAME}`,
    valueGetter: (params: TypedValueGetterParams<TradingEntityClearingDetail>) =>
      params.data && params.data.clearingHouse && params.data.clearingHouse.publishedName,
    headerName: Locale.translate(Translation.TC_CCP),
    width: 90,
    filter: 'agSetColumnFilter'
  },
  {
    colId: `${prefix}${TradingEntityClearingPanelGridColumnId.SWIFT_ID}`,
    valueGetter: (params: TypedValueGetterParams<TradingEntityClearingDetail>) =>
      params.data && params.data.tradingEntityClearing.swiftMessageId,
    headerName: Locale.translate(Translation.TC_CODE),
    width: 135,
    filter: 'agSetColumnFilter'
  }
]

const productSectorColumnDef = (prefix: string): ColDef => ({
  colId: `${prefix}${TradingEntityClearingPanelGridColumnId.PRODUCT_SECTOR_COUNT}`,
  valueGetter: (params: TypedValueGetterParams<TradingEntityClearingDetail>) =>
    params.data && params.data.productSectors && params.data.productSectors.length,
  headerName: Locale.translate(Translation.TC_PRODUCT_SECTORS),
  width: 115,
  filter: 'agSetColumnFilter'
})

const sponsorColumnDef: ColDef = {
  colId: TradingEntityClearingPanelGridColumnId.SPONSOR_NAME,
  valueGetter: (params: TypedValueGetterParams<TradingEntityClearingDetail>) =>
    params.data && params.data.sponsor && params.data.sponsor.publishedName,
  headerName: Locale.translate(Translation.TC_SPONSORING_BANK),
  width: 120,
  filter: 'agSetColumnFilter'
}

export const directTradingEntityClearingPanelColumnDefs: (ColGroupDef | ColDef)[] = [
  ...commonTradingEntityClearingPanelColumnDefs(DIRECT_PREFIX),
  productSectorColumnDef(DIRECT_PREFIX)
]

export const sponsoredTradingEntityClearingPanelColumnDefs: (ColGroupDef | ColDef)[] = [
  ...commonTradingEntityClearingPanelColumnDefs(SPONSORED_PREFIX),
  sponsorColumnDef,
  productSectorColumnDef(SPONSORED_PREFIX)
]

export const tradingEntityClearingGridOptions: GridOptions<TradingEntityClearingDetail> = {
  ...gridOptionsBase,
  groupDefaultExpanded: 0,
  getRowId: ({ data }) => data.tradingEntityClearing.id,
  suppressMaintainUnsortedOrder: true
}
